import {
  useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { useIsMapInteractionActive } from '~/_shared/utils/hooks/useIsMapInteractionActive';
import { useOverlappingProximitiesAtLocation } from '~/map/map/proximity/useOverlappingProximitiesAtLocation';
import { activeMapElementsSetActiveProximity } from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import {
  individualRadiusCenterHovered, individualRadiusCenterHoverEnded,
} from '~/store/frontendState/proximityTool/proximityTool.actionCreators';

export const useCenterInteraction = ({ proximityId, renderedCenter, rowId }: {
  proximityId: string;
  renderedCenter: WebglOverlayMarker | null;
  rowId?: SpreadsheetRowId;
}) => {
  const dispatch = useDispatch();
  const [isMouseOverCenter, setIsMouseOverCenter] = useState(false);

  const isMapInteractivityDisabled = useIsMapInteractionActive();
  const setOverlappingProximitiesLocation = useOverlappingProximitiesAtLocation();

  const handleHoverStart = useCallback(() => {
    dispatch(individualRadiusCenterHovered());
    setIsMouseOverCenter(true);
  }, [dispatch]);

  const handleHoverEnd = useCallback(() => {
    dispatch(individualRadiusCenterHoverEnded());
    setIsMouseOverCenter(false);
  }, [dispatch]);

  const handleClick = useCallback(() => {
    setOverlappingProximitiesLocation(null);
    dispatch(activeMapElementsSetActiveProximity(proximityId, rowId));
  }, [dispatch, setOverlappingProximitiesLocation, proximityId, rowId]);

  // Attach center event listeners
  useEffect(() => {
    if (renderedCenter && !isMapInteractivityDisabled) {
      renderedCenter.addEventListener('click', handleClick);
      renderedCenter.addEventListener('mouseout', handleHoverEnd);
      renderedCenter.addEventListener('mouseover', handleHoverStart);

      return () => {
        renderedCenter.removeEventListener('click', handleClick);
        renderedCenter.removeEventListener('mouseout', handleHoverEnd);
        renderedCenter.removeEventListener('mouseover', handleHoverStart);
      };
    }

    return undefined;
  }, [renderedCenter, handleHoverStart, handleHoverEnd, handleClick, isMapInteractivityDisabled]);

  return { isMouseOverCenter };
};
